// EmailTemplatesManager.js — Manage reusable email templates in Admin
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './EmailTemplatesManager.css';

const blankTemplate = { name: '', subject: '', body: '' };

export default function EmailTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | { ...template } | 'new'
  const [form, setForm] = useState(blankTemplate);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/email-templates`);
      if (res.ok) setTemplates(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(blankTemplate);
    setEditing('new');
    setError('');
  };

  const openEdit = (t) => {
    setForm({ name: t.name, subject: t.subject, body: t.body });
    setEditing(t);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      setError('Name, subject, and body are all required.');
      return;
    }
    setSaving(true);
    try {
      const isNew = editing === 'new';
      const url = isNew
        ? `${API_BASE_URL}/api/email-templates`
        : `${API_BASE_URL}/api/email-templates/${editing.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        await load();
        setEditing(null);
        setSuccess(isNew ? 'Template created.' : 'Template updated.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const d = await res.json();
        setError(d.error || 'Failed to save.');
      }
    } catch { setError('Network error.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/email-templates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        setDeleteConfirm(null);
        setSuccess('Template deleted.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="etm-container">
      <div className="etm-header">
        <div>
          <h2 className="etm-title">Email Templates</h2>
          <p className="etm-subtitle">
            Manage reusable email templates for common applicant communications.
            Use <code>{'{applicant_name}'}</code>, <code>{'{move_in_date}'}</code> as placeholders.
          </p>
        </div>
        <button className="etm-add-btn" onClick={openNew}>+ New Template</button>
      </div>

      {success && <div className="etm-success">{success}</div>}

      {/* Edit / New Form */}
      {editing && (
        <form className="etm-form" onSubmit={handleSave}>
          <div className="etm-form-header">
            <h3>{editing === 'new' ? 'New Template' : `Edit: ${editing.name}`}</h3>
            <button type="button" className="etm-close-btn" onClick={() => setEditing(null)}>×</button>
          </div>
          <div className="etm-form-body">
            <div className="etm-field">
              <label>Template Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Application Approved"
              />
            </div>
            <div className="etm-field">
              <label>Subject Line *</label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                placeholder="e.g. Your rental application has been approved!"
              />
            </div>
            <div className="etm-field">
              <label>Body *</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                placeholder="Write the email body here. Use {applicant_name} as a placeholder."
                rows={10}
              />
            </div>
          </div>
          {error && <div className="etm-error">{error}</div>}
          <div className="etm-form-actions">
            <button type="button" className="etm-cancel-btn" onClick={() => setEditing(null)}>Cancel</button>
            <button type="submit" className="etm-save-btn" disabled={saving}>
              {saving ? 'Saving…' : (editing === 'new' ? 'Create Template' : 'Save Changes')}
            </button>
          </div>
        </form>
      )}

      {/* Templates List */}
      {loading ? (
        <div className="etm-empty">Loading templates…</div>
      ) : templates.length === 0 ? (
        <div className="etm-empty">No templates yet. Click "+ New Template" to create the first one.</div>
      ) : (
        <div className="etm-list">
          {templates.map((t) => (
            <div key={t.id} className="etm-card">
              <div className="etm-card-left">
                <div className="etm-card-icon">📧</div>
              </div>
              <div className="etm-card-body">
                <div className="etm-card-header">
                  <span className="etm-card-name">{t.name}</span>
                  <span className="etm-card-subject">{t.subject}</span>
                </div>
                <p className="etm-card-preview">{t.body.slice(0, 140)}{t.body.length > 140 ? '…' : ''}</p>
              </div>
              <div className="etm-card-actions">
                <button className="etm-edit-btn" onClick={() => openEdit(t)}>Edit</button>
                {deleteConfirm === t.id ? (
                  <div className="etm-delete-confirm">
                    <span>Delete?</span>
                    <button className="etm-confirm-del" onClick={() => handleDelete(t.id)}>Yes</button>
                    <button className="etm-cancel-del" onClick={() => setDeleteConfirm(null)}>No</button>
                  </div>
                ) : (
                  <button className="etm-del-btn" onClick={() => setDeleteConfirm(t.id)}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
